// Incoming comms feed — a compact, top-center, auto-fading message strip.
// Bolo3K has no chat history panel; this is the phone-sized equivalent of
// the desktop chat overlay (client/src/chatUI.ts renderHistory), capped at
// 4 lines with an ~8 s fade.
//
// The comms module can't subscribe to the Connection itself (main.ts owns
// the handler slots), so this class is driven externally: the architect
// wires the connection's s2c.chat events to CommsApi.showIncoming in
// Wave 3, which lands here.

/** Lines start fading this long after arrival (ms). */
const FADE_AFTER_MS = 8_000;
/** Opacity transition length — keep in sync with .cw-feed-line in comms.css. */
const FADE_DURATION_MS = 700;
/** Max lines on screen; oldest is dropped when a new one arrives. */
const MAX_LINES = 4;

export class CommsFeed {
  private readonly el: HTMLDivElement;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'cw-feed';
    parent.appendChild(this.el);
  }

  /** Player chat line: "name: text" (allies-scope lines get an [allies]
   *  tag and tint, mirroring the desktop chat overlay). */
  showIncoming(from: string, text: string, scope: string): void {
    const line = document.createElement('div');
    line.className = 'cw-feed-line';
    const tag = document.createElement('span');
    tag.className = 'cw-feed-from';
    if (scope === 'allies') {
      line.classList.add('cw-allies');
      tag.textContent = `${from} [allies]:`;
    } else {
      tag.textContent = `${from}:`;
    }
    const body = document.createElement('span');
    body.textContent = ` ${text}`;
    line.append(tag, body);
    this.push(line);
  }

  /** System/wire-event line (alliance formed, player joined, …). */
  showSystem(text: string): void {
    const line = document.createElement('div');
    line.className = 'cw-feed-line cw-feed-sys';
    line.textContent = text;
    this.push(line);
  }

  dispose(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.el.remove();
  }

  // ---- internals ---------------------------------------------------

  private push(line: HTMLElement): void {
    this.el.appendChild(line);
    while (this.el.children.length > MAX_LINES) this.el.firstChild?.remove();
    const fade = setTimeout(() => {
      this.timers.delete(fade);
      line.classList.add('cw-fade');
      const drop = setTimeout(() => {
        this.timers.delete(drop);
        line.remove();
      }, FADE_DURATION_MS);
      this.timers.add(drop);
    }, FADE_AFTER_MS);
    this.timers.add(fade);
  }
}
